import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, DatePicker, Space, Typography, Row, Col, Statistic, Spin, Input, Button, Timeline, Avatar } from 'antd';
import { UserOutlined, SearchOutlined, ReloadOutlined, ClockCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../utils/api';
import { useDeepLinkWhen } from '../utils/deeplink';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const actionColors = {
  LOGIN: 'blue', CREATE_SONG: 'green', BULK_UPLOAD_SONGS: 'green',
  UPLOAD_THUMBNAILS: 'purple', UPDATE_DIST_STATUS: 'orange', UPDATE_IPRS_STATUS: 'gold',
  CREATE_AGREEMENT: 'cyan', DELETE_AGREEMENT: 'red', CREATE_USER: 'geekblue',
  DELETE_USER: 'red', EXCEL_UPLOAD: 'lime', CREATE_CHANNEL: 'volcano',
  ASSIGN_CHANNELS: 'magenta', UPDATE_PERMISSIONS: 'geekblue', ADD_INCOME: 'green',
  ADD_EXPENSE: 'orange', DELETE_EXPENSE: 'red', DELETE_THUMBNAIL: 'red',
  UPDATE_PROFILE: 'blue', CHANGE_PASSWORD: 'blue', update: 'default',
  ADD_COMPANY: 'cyan', DELETE_COMPANY: 'red'
};

const actionLabels = {
  LOGIN: 'Login', CREATE_SONG: 'Song Upload', BULK_UPLOAD_SONGS: 'Bulk Song Upload',
  UPLOAD_THUMBNAILS: 'Poster Upload', UPDATE_DIST_STATUS: 'Distribution Change',
  UPDATE_IPRS_STATUS: 'IPRS Update', CREATE_AGREEMENT: 'Agreement Added',
  DELETE_AGREEMENT: 'Agreement Deleted', CREATE_USER: 'User Created',
  DELETE_USER: 'User Deleted', EXCEL_UPLOAD: 'Excel Upload', CREATE_CHANNEL: 'Channel Added',
  ASSIGN_CHANNELS: 'Channel Assigned', UPDATE_PERMISSIONS: 'Permissions Updated',
  ADD_INCOME: 'Income Added', ADD_EXPENSE: 'Expense Added', DELETE_EXPENSE: 'Expense Deleted',
  DELETE_THUMBNAIL: 'Thumbnail Deleted', UPDATE_PROFILE: 'Profile Updated',
  CHANGE_PASSWORD: 'Password Changed', update: 'Update', ADD_COMPANY: 'Company Added',
  DELETE_COMPANY: 'Company Deleted', VIDEO_UPLOAD_LOG: 'Video Upload',
  ASSIGN_CHANNEL_STAFF: 'Channel Staff Assigned', STUDIO_PAYMENT: 'Studio Payment',
  UPDATE_SONG: 'Song Updated', SYNC_METADATA: 'Metadata Sync', BULK_METADATA: 'Bulk Metadata',
  CREATE_RELEASE: 'Release Scheduled', DELETE_RELEASE: 'Release Deleted',
  ADD_TARGET: 'Target Set', TASK_COMPLETE: 'Task Completed'
};

export default function ActivityLog() {
  const [data, setData] = useState({ logs: [], total: 0, actions: [], entity_types: [], staff_list: [], today_summary: [] });
  const [loading, setLoading] = useState(false);
  // AI answers open this page on the asked day (?date=).
  const linkDate = useDeepLinkWhen().date;
  const linkDateStr = linkDate ? linkDate.format('YYYY-MM-DD') : '';
  const [filters, setFilters] = useState({ user_id: '', action: '', date_from: linkDateStr, date_to: linkDateStr, page: 1 });
  const [viewMode, setViewMode] = useState('table');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.action) params.action = filters.action;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      params.page = filters.page;
      params.limit = 50;
      const res = await api.get('/activity-log', { params });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    {
      title: 'Time', dataIndex: 'created_at', width: 160,
      render: v => <span><ClockCircleOutlined style={{ marginRight: 4 }} />{dayjs(v).format('DD MMM, hh:mm A')}</span>
    },
    {
      title: 'Staff', dataIndex: 'full_name', width: 150,
      render: (v, r) => <span><Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 6, background: '#667eea' }} />{v || r.username}</span>
    },
    { title: 'Role', dataIndex: 'user_role', width: 110, render: v => <Tag>{v}</Tag> },
    {
      title: 'Action', dataIndex: 'action', width: 160,
      render: v => <Tag color={actionColors[v] || 'default'}>{actionLabels[v] || v}</Tag>
    },
    { title: 'Entity', dataIndex: 'entity_type', width: 100, render: v => v ? <Tag color="default">{v}</Tag> : '-' },
    {
      title: 'Details', dataIndex: 'details', ellipsis: true,
      render: (v, r) => {
        if (!v) return '-';
        let detail = v;
        try {
          const obj = JSON.parse(v);
          const parts = [];
          if (obj.title) parts.push(obj.title);
          if (obj.song_title) parts.push(obj.song_title);
          if (obj.status) parts.push(`→ ${obj.status}`);
          if (obj.staff_name) parts.push(`(by ${obj.staff_name})`);
          if (obj.target_user) parts.push(`→ ${obj.target_user}`);
          detail = parts.length > 0 ? parts.join(' ') : JSON.stringify(obj);
        } catch {}
        return <Text type="secondary" style={{ fontSize: 12 }}>{detail}</Text>;
      }
    },
    { title: 'Staff ID', dataIndex: 'user_id', width: 70, render: v => <Tag style={{ fontSize: 11 }}>#{v}</Tag> },
    { title: 'IP', dataIndex: 'ip_address', width: 120, render: v => <Text type="secondary" style={{ fontSize: 11 }}>{v || '-'}</Text> }
  ];

  const handleExport = () => {
    const rows = [['Time', 'Staff', 'Role', 'Action', 'Entity', 'Details', 'IP']];
    (data.logs || []).forEach(r => {
      rows.push([
        dayjs(r.created_at).format('YYYY-MM-DD HH:mm:ss'),
        r.full_name || r.username, r.user_role,
        actionLabels[r.action] || r.action, r.entity_type || '',
        r.details || '', r.ip_address || ''
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${(c||'').toString().replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `activity-log-${dayjs().format('YYYY-MM-DD')}.csv`; a.click();
  };

  return (
    <div>
      <Title level={3}>Staff Activity Log</Title>
      <Text type="secondary">Track who did what and when</Text>

      {/* Today's summary */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        {(data.today_summary || []).slice(0, 6).map((s, i) => (
          <Col xs={12} sm={8} md={4} key={i}>
            <Card size="small" hoverable onClick={() => setFilters(f => ({ ...f, user_id: s.user_id, page: 1 }))}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>{s.full_name}</span>}
                value={s.action_count}
                suffix="actions today"
                valueStyle={{ fontSize: 18, color: '#667eea' }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>Last: {dayjs(s.last_action).fromNow()}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="All Staff" allowClear style={{ width: 180 }}
            value={filters.user_id || undefined}
            onChange={v => setFilters(f => ({ ...f, user_id: v || '', page: 1 }))}
            showSearch optionFilterProp="label"
            options={(data.staff_list || []).map(s => ({ value: s.id, label: s.full_name || s.username }))}
          />
          <Select
            placeholder="All Actions" allowClear style={{ width: 180 }}
            value={filters.action || undefined}
            onChange={v => setFilters(f => ({ ...f, action: v || '', page: 1 }))}
            options={(data.actions || []).map(a => ({ value: a, label: actionLabels[a] || a }))}
          />
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setFilters(f => ({ ...f, date_from: dates[0].format('YYYY-MM-DD'), date_to: dates[1].format('YYYY-MM-DD'), page: 1 }));
              } else {
                setFilters(f => ({ ...f, date_from: '', date_to: '', page: 1 }));
              }
            }}
          />
          <Select value={viewMode} onChange={setViewMode} style={{ width: 120 }}
            options={[{ value: 'table', label: 'Table View' }, { value: 'timeline', label: 'Timeline View' }]}
          />
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>Export CSV</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
        </Space>
      </Card>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        viewMode === 'table' ? (
          <Table
            columns={columns}
            dataSource={data.logs || []}
            rowKey="id"
            size="small"
            pagination={{
              current: filters.page,
              total: data.total,
              pageSize: 50,
              onChange: (p) => setFilters(f => ({ ...f, page: p })),
              showTotal: (t) => `Total ${t} records`
            }}
            scroll={{ x: 900 }}
          />
        ) : (
          <Card>
            <Timeline>
              {(data.logs || []).map(log => (
                <Timeline.Item
                  key={log.id}
                  color={actionColors[log.action] === 'red' ? 'red' : actionColors[log.action] === 'green' ? 'green' : 'blue'}
                >
                  <p style={{ margin: 0 }}>
                    <Tag color={actionColors[log.action] || 'default'}>{actionLabels[log.action] || log.action}</Tag>
                    <Text strong>{log.full_name || log.username}</Text>
                    {log.details && <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{log.details}</Text>}
                  </p>
                  <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(log.created_at).format('DD MMM YYYY, hh:mm A')} - {dayjs(log.created_at).fromNow()}</Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        )
      )}
    </div>
  );
}
